import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader, Modal, EmptyState, formatDate, formatCurrency } from "@/components/pcp";
import { useFiltroEmpresa, SemVinculoToggle } from "@/components/EmpresaSelector";
import { usePodeVerValores } from "@/context/AuthContext";
import { Plus, TrashSimple, ArrowRight } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { escopoContrato } from "@/components/AppLayout";
import { toast } from "sonner";

const empty = { numero: "", contratante: "", tipo: "Público", objeto: "", data_inicio: "", data_fim: "", observacoes: "", status: "Contrato", origem: "manual", itens: [] };

export const StatusContrato = ({ status }) => {
  const s = status === "Orçamento" ? "Orçamento (legado)" : status || "Contrato";
  const cor = status === "Orçamento" ? { bg: "#FFFBEA", fg: "#B45309", bd: "#FFCC00" } : s === "Encerrado" ? { bg: "#F4F4F5", fg: "#52525B", bd: "#D4D4D8" } : { bg: "#ECFDF5", fg: "#047857", bd: "#6EE7B7" };
  return <span data-testid="contrato-status" style={{ padding: "2px 8px", borderRadius: 3, fontSize: "0.7rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", background: cor.bg, color: cor.fg, border: `1px solid ${cor.bd}` }}>{s}</span>;
};

export default function Contratos() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(empty);
  const urlFiltro = new URLSearchParams(window.location.search).get("filtro");
  const [filtro, setFiltro] = useState(urlFiltro === "orcamentos" ? "Orçamento" : "todos");
  const fEmp = useFiltroEmpresa(items);
  const vv = usePodeVerValores();
  useEffect(() => { setFiltro(urlFiltro === "orcamentos" ? "Orçamento" : "todos"); }, [urlFiltro]);
  const nav = useNavigate();

  const load = () => api.get("/contratos").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.numero || !form.contratante) { toast.error("Número e contratante são obrigatórios"); return; }
    try {
      const { data } = await api.post("/contratos", form);
      toast.success("Contrato criado — agora cadastre a planilha de itens");
      setModal(false); setForm(empty);
      nav(`/contratos/${data.id}`);
    } catch { toast.error("Erro ao salvar"); }
  };

  const remove = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm("Enviar para lixeira?")) return;
    await api.delete(`/contratos/${id}`);
    toast.success("Enviado para lixeira");
    load();
  };

  const lista = fEmp.lista.filter((c) => filtro === "todos" || (c.status || "Contrato") === filtro);
  const legados = items.filter((c) => c.status === "Orçamento").length;

  return (
    <>
      <PageHeader
        testid="page-contratos"
        title="Contratos"
        subtitle={`${items.filter((c) => (c.status || "Contrato") === "Contrato").length} contratos ativos · ${items.filter((c) => c.origem === "proposta").length} gerados de proposta${legados ? ` · ${legados} orçamento(s) legado(s) ainda nesta base` : ""}`}
        actions={user?.role !== "fiscal" && !escopoContrato(user) && <div style={{ display: "flex", gap: 8 }}>
          <Link to="/orcamentos" className="btn btn-outline" data-testid="btn-ir-orcamentos">Orçamentos e propostas</Link>
          <button className="btn btn-primary" onClick={() => { setForm(empty); setModal(true); }} data-testid="btn-new-contrato"><Plus size={16} weight="bold" /> Novo contrato</button>
        </div>}
      />
      <div className="page-body">
        <div style={{ display: "flex", gap: 6, marginBottom: 14 }}>
          {["todos", "Contrato", "Encerrado", ...(legados ? ["Orçamento"] : [])].map((f) => (
            <button key={f} className={`chip ${filtro === f ? "chip-active" : ""}`} onClick={() => setFiltro(f)} data-testid={`filtro-contrato-${f}`} style={{ cursor: "pointer", fontWeight: filtro === f ? 700 : 500, background: filtro === f ? "#0A0A0A" : undefined, color: filtro === f ? "#FFCC00" : undefined }}>{f === "todos" ? "Todos" : f === "Orçamento" ? "Orçamentos (legado)" : f + "s"}</button>
          ))}
          <SemVinculoToggle filtro={fEmp} rotulo="contrato(s)" />
        </div>
        {lista.length === 0 ? (
          <EmptyState title="Nenhum contrato" description="Contratos nascem de uma proposta vencedora (Comercial → Propostas → Gerar contrato) ou são cadastrados aqui quando já existem fora do sistema." />
        ) : (
          <div className="pcp-card" style={{ overflowX: "auto" }}>
            <table className="pcp-table" data-testid="contratos-table">
              <thead><tr><th>Status</th><th>Número</th><th>Contratante</th><th>Origem</th><th>Objeto</th><th style={{ textAlign: "right" }}>Itens</th><th style={{ textAlign: "right" }}>OS</th>{vv && <th style={{ textAlign: "right" }}>Valor</th>}<th>Período</th><th></th></tr></thead>
              <tbody>
                {lista.map((c) => (
                  <tr key={c.id} onClick={() => nav(`/contratos/${c.id}`)} style={{ cursor: "pointer" }} data-testid={`contrato-row-${c.id}`}>
                    <td><StatusContrato status={c.status} /></td>
                    <td className="font-mono font-semibold">{c.numero}</td>
                    <td>{c.contratante}</td>
                    <td style={{ fontSize: "0.72rem", color: "#71717A" }} data-testid={`contrato-origem-${c.id}`}>{c.origem === "proposta" ? "via proposta" : c.origem === "legado" ? "legado" : c.origem === "manual" ? "manual" : "—"}</td>
                    <td style={{ maxWidth: 320, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{c.objeto}</td>
                    <td className="font-mono" style={{ textAlign: "right" }}>{(c.itens || []).length}</td>
                    <td className="font-mono" style={{ textAlign: "right" }}>{c.qtd_os || 0}</td>
                    {vv && <td className="font-mono" style={{ textAlign: "right" }}>{formatCurrency(c.valor)}</td>}
                    <td className="font-mono text-xs">{formatDate(c.data_inicio)} → {formatDate(c.data_fim)}</td>
                    <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                      <Link to={`/contratos/${c.id}`} className="btn btn-ghost btn-sm" onClick={(e) => e.stopPropagation()}><ArrowRight size={14} weight="bold" /></Link>
                      {user?.role !== "fiscal" && <button className="btn btn-ghost btn-sm" onClick={(e) => remove(e, c.id)} data-testid={`btn-remover-contrato-${c.id}`}><TrashSimple size={14} weight="bold" color="#DC2626" /></button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal open={modal} onClose={() => setModal(false)} title="Novo contrato (já contratado)">
        <div style={{ display: "grid", gap: 12 }}>
          <div style={{ fontSize: "0.78rem", color: "#57534E" }} data-testid="novo-contrato-aviso">Use apenas para contratos já firmados fora do fluxo comercial. Para orçar e propor, vá em <Link to="/orcamentos" style={{ textDecoration: "underline" }}>Orçamentos</Link>.</div>
          <F label="Número *"><input className="input" value={form.numero} onChange={(e) => setForm({ ...form, numero: e.target.value })} data-testid="contrato-numero" /></F>
          <F label="Contratante *"><input className="input" value={form.contratante} onChange={(e) => setForm({ ...form, contratante: e.target.value })} data-testid="contrato-contratante" /></F>
          <F label="Tipo"><select className="select" value={form.tipo} onChange={(e) => setForm({ ...form, tipo: e.target.value })}><option value="Público">Público</option><option value="Privado">Privado</option><option value="Concessão">Concessão</option></select></F>
          <F label="Objeto"><textarea className="textarea" rows={2} value={form.objeto} onChange={(e) => setForm({ ...form, objeto: e.target.value })} /></F>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <F label="Início"><input type="date" className="input" value={form.data_inicio || ""} onChange={(e) => setForm({ ...form, data_inicio: e.target.value })} /></F>
            <F label="Fim"><input type="date" className="input" value={form.data_fim || ""} onChange={(e) => setForm({ ...form, data_fim: e.target.value })} /></F>
          </div>
          <F label="Observações"><textarea className="textarea" rows={2} value={form.observacoes} onChange={(e) => setForm({ ...form, observacoes: e.target.value })} /></F>
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 24 }}>
          <button className="btn btn-outline" onClick={() => setModal(false)}>Cancelar</button>
          <button className="btn btn-primary" onClick={save} data-testid="btn-save-contrato">Criar e cadastrar itens</button>
        </div>
      </Modal>
    </>
  );
}

function F({ label, children }) { return <div><div className="label">{label}</div>{children}</div>; }
